class FAQSection extends HTMLElement {
  constructor() {
    super();
    this.searchBox = this.querySelector('[data-faq-search]');
    this.categoryFilters = this.querySelectorAll('[data-category]');
    this.faqItems = this.querySelectorAll('[data-faq-item]');
    this.categoryHeaders = this.querySelectorAll('[data-category-header]');
    this.noResultsMessage = this.querySelector('.faq-no-results');
    
    this.currentCategory = 'all';
    this.currentSearchTerm = '';
    
    this.init();
  }

  init() {
    if (this.searchBox) {
      this.setupSearch();
    }
    
    if (this.categoryFilters.length > 0) {
      this.setupCategoryFilters();
    }
    
    this.setupAccessibility();
    this.setupAnimations();
    
    // Show all items initially
    this.updateDisplay();
  }

  setupSearch() {
    // Debounce search to avoid excessive filtering
    let searchTimeout;
    
    this.searchBox.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.currentSearchTerm = e.target.value.toLowerCase().trim();
        this.updateDisplay();
      }, 300);
    });

    // Clear search on escape key
    this.searchBox.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.searchBox.value = '';
        this.currentSearchTerm = '';
        this.updateDisplay();
      }
    });
  }

  setupCategoryFilters() {
    this.categoryFilters.forEach(filter => {
      filter.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Update active state
        this.categoryFilters.forEach(f => f.classList.remove('active'));
        filter.classList.add('active');
        
        // Update current category
        this.currentCategory = filter.dataset.category;
        this.updateDisplay();
      });
    });
  }

  setupAccessibility() {
    // Add ARIA labels and roles
    this.faqItems.forEach((item, index) => {
      const details = item.querySelector('details');
      const summary = item.querySelector('summary');
      const content = item.querySelector('.faq-answer');
      
      if (details && summary && content) {
        const id = `faq-${Date.now()}-${index}`;
        
        details.setAttribute('id', `details-${id}`);
        summary.setAttribute('aria-controls', `content-${id}`);
        content.setAttribute('id', `content-${id}`);
        
        // Add keyboard navigation
        summary.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            details.open = !details.open;
          }
        });
        
        // Update aria-expanded when opened/closed
        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', details.open);
        });
        
        // Set initial aria-expanded state
        summary.setAttribute('aria-expanded', details.open);
      }
    });

    // Add search box accessibility
    if (this.searchBox) {
      this.searchBox.setAttribute('role', 'searchbox');
      this.searchBox.setAttribute('aria-label', 'Search FAQ items');
      
      // Announce search results to screen readers
      const searchResultsId = `search-results-${Date.now()}`;
      this.searchBox.setAttribute('aria-describedby', searchResultsId);
      
      const searchResults = document.createElement('div');
      searchResults.id = searchResultsId;
      searchResults.className = 'sr-only';
      searchResults.setAttribute('aria-live', 'polite');
      this.appendChild(searchResults);
      
      this.searchResultsAnnouncer = searchResults;
    }
  }

  setupAnimations() {
    // Intersection Observer for scroll animations if supported
    if ('IntersectionObserver' in window) {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.animationDelay = `${Array.from(this.faqItems).indexOf(entry.target) * 0.1}s`;
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      this.faqItems.forEach(item => {
        observer.observe(item);
      });
    }
  }

  updateDisplay() {
    let visibleCount = 0;
    let visibleCategories = new Set();

    this.faqItems.forEach(item => {
      const itemCategory = item.dataset.category || 'general';
      const searchContent = item.dataset.searchContent || '';
      
      let shouldShow = true;

      // Filter by category
      if (this.currentCategory !== 'all' && itemCategory !== this.currentCategory) {
        shouldShow = false;
      }

      // Filter by search term
      if (this.currentSearchTerm && !searchContent.includes(this.currentSearchTerm)) {
        shouldShow = false;
      }

      // Show/hide item
      if (shouldShow) {
        item.classList.remove('hidden');
        visibleCount++;
        visibleCategories.add(itemCategory);
      } else {
        item.classList.add('hidden');
      }
    });

    // Handle category headers
    this.categoryHeaders.forEach(header => {
      const headerCategory = header.dataset.categoryHeader;
      if (this.currentCategory === 'all') {
        // Show headers that have visible items
        if (visibleCategories.has(headerCategory)) {
          header.classList.remove('hidden');
        } else {
          header.classList.add('hidden');
        }
      } else {
        // Hide all headers when filtering by category
        header.classList.add('hidden');
      }
    });

    // Show/hide no results message
    if (this.noResultsMessage) {
      if (visibleCount === 0) {
        this.noResultsMessage.style.display = 'block';
      } else {
        this.noResultsMessage.style.display = 'none';
      }
    }

    // Announce results to screen readers
    this.announceSearchResults(visibleCount);

    // Dispatch custom event for external integrations
    this.dispatchEvent(new CustomEvent('faqFiltered', {
      detail: {
        visibleCount,
        category: this.currentCategory,
        searchTerm: this.currentSearchTerm
      }
    }));
  }

  announceSearchResults(count) {
    if (this.searchResultsAnnouncer) {
      let message = '';
      
      if (this.currentSearchTerm) {
        if (count === 0) {
          message = 'No FAQ items found matching your search.';
        } else if (count === 1) {
          message = '1 FAQ item found.';
        } else {
          message = `${count} FAQ items found.`;
        }
      } else if (this.currentCategory !== 'all') {
        if (count === 0) {
          message = 'No FAQ items found in this category.';
        } else if (count === 1) {
          message = '1 FAQ item found in this category.';
        } else {
          message = `${count} FAQ items found in this category.`;
        }
      }
      
      this.searchResultsAnnouncer.textContent = message;
    }
  }

  // Public methods for external control
  setCategory(category) {
    this.currentCategory = category;
    
    // Update active filter button
    this.categoryFilters.forEach(filter => {
      if (filter.dataset.category === category) {
        filter.classList.add('active');
      } else {
        filter.classList.remove('active');
      }
    });
    
    this.updateDisplay();
  }

  setSearchTerm(term) {
    this.currentSearchTerm = term.toLowerCase().trim();
    
    if (this.searchBox) {
      this.searchBox.value = term;
    }
    
    this.updateDisplay();
  }

  clearFilters() {
    this.currentCategory = 'all';
    this.currentSearchTerm = '';
    
    if (this.searchBox) {
      this.searchBox.value = '';
    }
    
    // Reset active category filter
    this.categoryFilters.forEach(filter => {
      if (filter.dataset.category === 'all') {
        filter.classList.add('active');
      } else {
        filter.classList.remove('active');
      }
    });
    
    this.updateDisplay();
  }

  openFAQ(index) {
    const faqItem = this.faqItems[index];
    if (faqItem) {
      const details = faqItem.querySelector('details');
      if (details) {
        details.open = true;
        
        // Scroll into view
        setTimeout(() => {
          faqItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }, 100);
      }
    }
  }

  closeFAQ(index) {
    const faqItem = this.faqItems[index];
    if (faqItem) {
      const details = faqItem.querySelector('details');
      if (details) {
        details.open = false;
      }
    }
  }

  openAllFAQs() {
    this.faqItems.forEach(item => {
      if (!item.classList.contains('hidden')) {
        const details = item.querySelector('details');
        if (details) {
          details.open = true;
        }
      }
    });
  }

  closeAllFAQs() {
    this.faqItems.forEach(item => {
      const details = item.querySelector('details');
      if (details) {
        details.open = false;
      }
    });
  }
}

// Register the custom element
customElements.define('faq-section', FAQSection);

// Enhanced FAQ functionality for better UX
document.addEventListener('DOMContentLoaded', function() {
  // Auto-initialize FAQ sections
  const faqSections = document.querySelectorAll('.faq-section');
  
  faqSections.forEach(section => {
    // Convert to custom element if not already
    if (!customElements.get('faq-section')) {
      section.classList.add('faq-section-enhanced');
    }
    
    // Add keyboard shortcuts
    section.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchBox = section.querySelector('[data-faq-search]');
        if (searchBox) {
          searchBox.focus();
        }
      }
      
      // Escape to clear search
      if (e.key === 'Escape') {
        const searchBox = section.querySelector('[data-faq-search]');
        if (searchBox && document.activeElement === searchBox) {
          searchBox.value = '';
          searchBox.dispatchEvent(new Event('input'));
        }
      }
    });
    
    // Add accordion behavior with smooth animations
    const allSummaries = section.querySelectorAll('.faq-question');
    allSummaries.forEach(summary => {
      summary.addEventListener('click', function(e) {
        e.preventDefault();
        
        const clickedDetails = this.closest('details');
        const clickedAnswer = clickedDetails.querySelector('.faq-answer');
        const allDetails = section.querySelectorAll('details');
        const isCurrentlyOpen = clickedDetails.open;
        
        if (isCurrentlyOpen) {
          // Closing animation
          smoothClose(clickedDetails, clickedAnswer);
        } else {
          // Close all other FAQs first
          allDetails.forEach(details => {
            if (details !== clickedDetails && details.open) {
              const answer = details.querySelector('.faq-answer');
              smoothClose(details, answer);
            }
          });
          
          // Open the clicked FAQ
          setTimeout(() => {
            smoothOpen(clickedDetails, clickedAnswer);
          }, 100);
        }
      });
    });
    
    // Smooth opening function
    function smoothOpen(details, answer) {
      details.open = true;
      details.classList.add('smooth-opening');
      
      // Get the natural height
      const height = answer.scrollHeight;
      answer.style.height = '0px';
      
      // Force reflow
      answer.offsetHeight;
      
      // Animate to full height
      answer.style.height = height + 'px';
      
      // Clean up after animation
      setTimeout(() => {
        answer.style.height = 'auto';
        details.classList.remove('smooth-opening');
      }, 400);
    }
    
    // Smooth closing function
    function smoothClose(details, answer) {
      details.classList.add('smooth-closing');
      
      // Set current height
      const height = answer.scrollHeight;
      answer.style.height = height + 'px';
      
      // Force reflow
      answer.offsetHeight;
      
      // Animate to 0 height
      answer.style.height = '0px';
      
      // Close after animation
      setTimeout(() => {
        details.open = false;
        details.classList.remove('smooth-closing');
        answer.style.height = '';
      }, 400);
    }
    
    // Add smooth scrolling to FAQ items when opened
    const faqDetails = section.querySelectorAll('details');
    faqDetails.forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          setTimeout(() => {
            details.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }, 450);
        }
      });
    });
  });
  
  // Add URL hash support for direct FAQ linking
  if (window.location.hash) {
    const hashTarget = document.querySelector(window.location.hash);
    if (hashTarget && hashTarget.closest('.faq-item')) {
      const details = hashTarget.closest('.faq-item').querySelector('details');
      if (details) {
        details.open = true;
        setTimeout(() => {
          hashTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 300);
      }
    }
  }
  
  // Add copy FAQ link functionality
  faqSections.forEach(section => {
    const faqItems = section.querySelectorAll('[data-faq-item]');
    faqItems.forEach((item, index) => {
      const details = item.querySelector('details');
      if (details) {
        // Add context menu for copying FAQ link
        item.addEventListener('contextmenu', (e) => {
          if (e.shiftKey) {
            e.preventDefault();
            const url = new URL(window.location);
            url.hash = `#${details.id}`;
            
            if (navigator.clipboard) {
              navigator.clipboard.writeText(url.toString()).then(() => {
                // Show temporary tooltip
                const tooltip = document.createElement('div');
                tooltip.textContent = 'FAQ link copied!';
                tooltip.style.cssText = `
                  position: absolute;
                  background: rgba(0,0,0,0.8);
                  color: white;
                  padding: 8px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                  z-index: 1000;
                  pointer-events: none;
                `;
                tooltip.style.left = e.pageX + 'px';
                tooltip.style.top = (e.pageY - 30) + 'px';
                
                document.body.appendChild(tooltip);
                setTimeout(() => tooltip.remove(), 2000);
              });
            }
          }
        });
      }
    });
  });
});

// Export for external use
window.FAQSection = FAQSection;

